/**
 * Tree node types for generic tree view
 */
export type TreeNodeType =
  | 'connection'
  | 'database'
  | 'databases-folder'
  | 'tables-folder'
  | 'views-folder'
  | 'programmability-folder'
  | 'stored-procedures-folder'
  | 'functions-folder'
  // Schema-first grouping
  | 'schema-container-folder'
  // Table subfolders
  | 'columns-folder'
  | 'keys-folder'
  | 'constraints-folder'
  | 'triggers-folder'
  | 'indexes-folder'
  | 'statistics-folder'
  | 'partitions-folder'
  // Security folders (server-level)
  | 'server-security-folder'
  | 'logins-folder'
  | 'server-roles-folder'
  // Security folders (PostgreSQL server-level)
  | 'pg-roles-folder'
  | 'login-roles-folder'
  | 'group-roles-folder'
  // Security folders (database-level)
  | 'database-security-folder'
  | 'users-folder'
  | 'database-roles-folder'
  | 'schemas-folder'
  // Database objects
  | 'table'
  | 'view'
  | 'stored-procedure'
  | 'function'
  | 'column'
  | 'key'
  | 'constraint'
  | 'index'
  | 'trigger'
  | 'statistic'
  | 'partition'
  // Security objects
  | 'login'
  | 'server-role'
  | 'database-user'
  | 'database-role'
  | 'schema'
  | 'login-role'
  | 'group-role'
  // SQL Server Agent
  | 'agent-jobs-folder'
  | 'agent-jobs-subfolder'
  | 'agent-job'
  | 'agent-operator'
  | 'agent-alert'
  | 'agent-proxy'
  // S23 — conditional "Dashboards" root node (visible only when >=1 dashboard exists)
  | 'dashboards-root'
  | 'dashboard'
  // Azure Storage (spec §5.1)
  | 'storage-service-folder'
  | 'storage-container'
  | 'storage-table'
  | 'storage-queue'
  | 'storage-share'
  | 'storage-action'
  // Messages
  | 'message'
  | 'error';

/**
 * Context menu action definition
 */
export interface ContextMenuAction {
  id: string;
  label: string;
  icon?: string; // Icon identifier
  isSeparator?: boolean; // Whether this item is a separator
  submenu?: ContextMenuAction[]; // Optional submenu items
  /**
   * When true, the item is shown greyed-out and its handler is not invoked
   * on select. Repo convention: every disabled item MUST set `disabledReason`
   * so the row can explain why (surfaced via a `title` attribute — see
   * `TreeNodeContextMenu`). See S23 "Duplicate" at the free-dashboard cap.
   */
  disabled?: boolean;
  /** Explanation shown (via `title`, and as a second line under the label) when `disabled` is true. */
  disabledReason?: string;
  /** A destructive action (delete / drop): rendered in the destructive colour, as the last group of its menu. */
  destructive?: boolean;
}

/**
 * Tree node model for tree view component
 */
export interface TreeNode {
  id: string;
  parentId: string | null;
  name: string;
  type: TreeNodeType | string; // Using union to support backward compatibility
  children?: TreeNode[];
  isLoading?: boolean;
  isExpanded?: boolean;
  metadata?: Record<string, any>;
  icon?: string; // Optional icon identifier
  contextMenuActions?: ContextMenuAction[]; // Available context menu actions
  canHaveChildren?: boolean; // Indicates node can have children even if children array is empty initially
}

/**
 * Tree data model
 */
export interface TreeModel {
  nodes: TreeNode[];
  expandedNodeIds: Set<string>;
}

/**
 * Type guard to check if a node is of a specific type
 */
export function isNodeOfType<T extends TreeNode>(
  node: TreeNode, 
  nodeType: TreeNodeType | string
): node is T {
  return node.type === nodeType;
}

/**
 * Check if a node is any type of folder
 */
export function isFolderNode(node: TreeNode): boolean {
  return node.type.endsWith('-folder');
}

/**
 * Check if a node is a specific folder type
 */
export function isFolderNodeOfType(node: TreeNode, folderType: string): boolean {
  return node.type === `${folderType}-folder`;
}

/**
 * Utility function to create child nodes for a parent node
 */
export function createChildNodes(
  parentId: string,
  nodeDefinitions: Array<{name: string, type: TreeNodeType | string, id?: string, children?: Array<{name: string, type: TreeNodeType | string, id?: string}>}>
): TreeNode[] {
  return nodeDefinitions.map(def => {
    const nodeId = def.id || `${parentId}:${def.name.toLowerCase().replace(/\s+/g, '-')}`;
    const node: TreeNode = {
      id: nodeId,
      parentId,
      name: def.name,
      type: def.type,
      children: def.children ? createChildNodes(
        nodeId, 
        def.children.map(child => ({
          name: child.name,
          type: child.type,
          id: child.id
        }))
      ) : [],
    };
    
    return node;
  });
} 
